/**
 * The automation surface as fixture data.
 *
 * It exists for the same reason the rest of FakeApi does: the app must be demonstrable and
 * testable with no backend, and gating must be *provable* offline. So this does not merely
 * return rows — it enforces the same abilities it advertises. A flow refuses to run when the
 * workspace policy withholds it; a run refuses a verb its own `can` did not offer; deleting a
 * flow that is not archived fails here exactly as it fails on the platform.
 */
import type {
  ActivityQuery,
  FlowRunRequest,
  LedgerQuery,
  ReportQuery,
  RunApproval,
  RunControl,
  RunQuery,
  ScheduledTaskDraft,
} from "~/api/client";
import { AutomationError, ForbiddenError, NotFoundError } from "~/api/errors";
import type {
  ActionRow,
  ArtifactContent,
  BudgetAck,
  ChildRunSummary,
  DigestTaskDraft,
  Directive,
  FlowTaskDraft,
  IdeSession,
  JumpAck,
  JumpRequest,
  LedgerPage,
  LedgerReport,
  NavigateTarget,
  PublishOptions,
  PublishResult,
  RouteProposal,
  RouteSurface,
  RunArtifact,
  RunDetail,
  RunEfficiency,
  RunTree,
  StepAck,
  StopAck,
  TaskBoard,
  TaskRow,
} from "~/api/opsTypes";
import {
  ABILITY,
  type Abilities,
  type ActivityEvent,
  type ActivityPage,
  type Agent,
  type Dispatch,
  type Flow,
  type FlowDefinitionResult,
  type FlowDetail,
  type FlowDocAttachment,
  type FlowList,
  type FlowNodeHistory,
  type FlowRunAck,
  type FlowSaveResult,
  type FlowStatus,
  type FlowVersion,
  type Run,
  type RunLogLine,
  type RunStats,
  type RunStep,
  type ScheduledTask,
} from "~/api/types";

const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");
const hoursAgo = (h: number) => new Date(BASE_TIME - h * 3600_000).toISOString();
const hoursAhead = (h: number) => new Date(BASE_TIME + h * 3600_000).toISOString();

const AGENTS: Agent[] = [
  {
    id: "lclgemma",
    name: "lclGemma",
    description: "Local · ollama · gemma4:26b",
    provider: "ollama",
    category: null,
    local: true,
    usable: true,
    avatarUrl: null,
  },
  {
    id: "lclqwen",
    name: "lclQwen (fast)",
    description: "Local · ollama · qwen2.5:7b-instruct",
    provider: "ollama",
    category: null,
    local: true,
    usable: true,
    avatarUrl: null,
  },
  {
    id: "reviewer",
    name: "Atlas Review",
    description: "Reads a change and argues with it before anybody else has to.",
    provider: "anthropic",
    category: "Development",
    local: false,
    usable: true,
    avatarUrl: null,
  },
  {
    // Visible but inert: proves the UI distinguishes "exists" from "you may use it".
    id: "researcher",
    name: "Atlas Research",
    description: "Long-form research persona. Not granted to this workspace.",
    provider: "openai",
    category: "Research",
    local: false,
    usable: false,
    avatarUrl: null,
  },
];

interface FlowSeed {
  slug: string;
  name: string;
  description: string;
  status: FlowStatus;
  nodes: Array<{ id: string; name: string; model: string | null; requireApproval?: boolean }>;
  versions: number;
  updated: number;
  /** R4 — the entity this flow serves. Absent = the 'workspace' sentinel. */
  entityLinkKind?: string;
  entityId?: string;
  categories?: string[];
  /**
   * W3 — the whole definition, not just the node summary the list needs. The studio is
   * unusable against fakes without one: it edits a definition, and a summary is what a
   * definition looks like after everything interesting has been thrown away.
   */
  definition?: Record<string, unknown>;
  /** Documentation attached through `attachDoc`. */
  documentId?: string | null;
}

/**
 * `morning-brief`'s real definition (W3 §E.5) — deliberately not a toy. Two sources of
 * different kinds, a conditioned edge that loops back, an approval gate, a `compute` node the
 * IDE host cannot execute, an output block, per-node budgets and a shared run ledger: one
 * fixture that gives every inspector tab something true to show, plus the un-surfaced fields
 * (`layout`, `policy`, `maxNodes`) a round-trip test needs in order to prove anything.
 */
const MORNING_BRIEF_DEFINITION: Record<string, unknown> = {
  id: "morning-brief",
  name: "Morning brief",
  startNodeId: "harvest",
  description: "Three feeds in, one table out, published before anybody is awake.",
  sharedContext: true,
  defaultPrompt: "Yesterday's close, in one table.",
  maxNodes: 24,
  policy: { concurrency: { max: 1, onOverlap: "skip" } },
  layout: {
    harvest: { x: 100, y: 80 },
    rank: { x: 176, y: 220 },
    publish: { x: 252, y: 360 },
    "src:feeds": { x: -140, y: 40 },
    "src:desk": { x: -140, y: 200 },
  },
  sources: [
    {
      sourceId: "feeds",
      kind: "rss",
      name: "Wires",
      config: {
        urls: ["https://feeds.reuters.com/reuters/topNews"],
        windowHours: 12,
        maxItems: 40,
      },
      consumers: ["harvest"],
    },
    {
      sourceId: "desk",
      kind: "manual",
      name: "Desk note",
      config: { value: "USDILS, TEVA, ESLT" },
      consumers: ["rank"],
    },
  ],
  nodes: [
    {
      nodeId: "harvest",
      name: "Harvest the wires",
      ownerRole: "ide:claude",
      toolName: "NoOpTool",
      model: "claude-haiku-4-5",
      effort: "low",
      purpose: "Collect, never judge.",
      instructions: "Read {{sources.feeds}} and list every item with its source name.\n",
      contextScope: { inherit: "none", maxResultTokens: 6000 },
      turnBudget: { soft: 6, hard: 12 },
      spill: "auto",
      transitions: [{ condition: "always", nextNodeId: "rank" }],
    },
    {
      nodeId: "rank",
      name: "Rank what matters",
      ownerRole: "ide:claude",
      toolName: "NoOpTool",
      model: "claude-sonnet-5",
      effort: "medium",
      instructions:
        "From {{harvest.result}} and the desk list {{sources.desk}}, keep the ten that move something.\nEnd with VERDICT: READY or VERDICT: THIN.",
      expect: "VERDICT:",
      contextScope: { inherit: "shared" },
      transitions: [
        {
          condition: 'contains(prev.result, "VERDICT: THIN")',
          nextNodeId: "harvest",
          label: "not enough — go again",
        },
        { condition: "always", nextNodeId: "publish" },
      ],
    },
    {
      nodeId: "publish",
      name: "Publish the table",
      kind: "compute",
      compute: { op: "passthrough" },
      requireApproval: true,
      instructions: "{{rank.result}}",
      transitions: [],
    },
  ],
  output: {
    kind: "document",
    collectionId: "col-1",
    mode: "create",
    titleTemplate: "{{flow.name}} — {{date}}",
    source: "finalResult",
    publishOn: "completed",
  },
};

/** A minimal definition for the seeds whose point is the manager, not the studio. */
function plainDefinition(seed: {
  slug: string;
  name: string;
  description: string;
  nodes: Array<{ id: string; name: string; model: string | null; requireApproval?: boolean }>;
}): Record<string, unknown> {
  return {
    id: seed.slug,
    name: seed.name,
    description: seed.description,
    startNodeId: seed.nodes[0]?.id ?? "",
    nodes: seed.nodes.map((node, index) => ({
      nodeId: node.id,
      name: node.name,
      ownerRole: "ide:claude",
      toolName: "NoOpTool",
      model: node.model,
      instructions: `Step ${index + 1} of ${seed.name}.`,
      ...(node.requireApproval ? { requireApproval: true } : {}),
      transitions:
        index + 1 < seed.nodes.length
          ? [{ condition: "always", nextNodeId: seed.nodes[index + 1]?.id ?? "" }]
          : [],
    })),
  };
}

const FLOW_SEEDS: FlowSeed[] = [
  {
    slug: "morning-brief",
    name: "Morning brief",
    description: "Three feeds in, one table out, published before anybody is awake.",
    status: "active",
    nodes: [
      { id: "harvest", name: "Harvest the feeds", model: "lclqwen" },
      { id: "rank", name: "Rank what matters", model: "lclgemma" },
      { id: "publish", name: "Publish the brief", model: null, requireApproval: true },
    ],
    versions: 4,
    updated: 6,
    definition: MORNING_BRIEF_DEFINITION,
  },
  {
    slug: "release-notes",
    name: "Release notes from the changelog",
    description: "Turns merged pull requests into prose a customer can read.",
    status: "draft",
    // R4 — one seed carries a real entity link so fake mode exercises the panel offline.
    entityLinkKind: "document",
    entityId: "doc-1",
    categories: ["delivery"],
    nodes: [
      { id: "collect", name: "Collect merged work", model: "lclqwen" },
      { id: "write", name: "Write the notes", model: "reviewer" },
    ],
    versions: 2,
    updated: 30,
  },
  {
    slug: "weekly-audit",
    name: "Weekly permission audit",
    description: "Reads who can reach what, and says what changed since last week.",
    status: "paused",
    nodes: [{ id: "audit", name: "Compare the grants", model: "lclgemma" }],
    versions: 1,
    updated: 96,
  },
  {
    slug: "old-experiment",
    name: "Retired experiment",
    description: "Archived. Deleting is only offered here — archive is the reversible step.",
    status: "archived",
    nodes: [{ id: "probe", name: "Probe", model: null }],
    versions: 1,
    updated: 400,
  },
];

interface RunSeed {
  id: string;
  name: string;
  kind: string;
  status: string;
  createdHoursAgo: number;
  durationMs: number | null;
  steps: { total: number; completed: number; failed: number };
  currentNodeId?: string;
  awaitingApprovalNodeId?: string;
  error?: string;
  degradedSources?: number;
  degraded?: Array<{ nodeId: string; sourceId: string; kind: string; reason: string }>;
  /** Set when this run was spawned by another (an amend or a subflow) — drives the back-stack. */
  causalRunId?: string;
  /** Presence is what the server's `can` state machine keys off for step/jump/pause/etc. */
  ideSessionKey?: string;
  breakpoints?: string[];
  tokenBudget?: number | null;
  budgetAction?: string;
  budgetTrippedAt?: string | null;
  /** A minimal graph snapshot — proves the run viewer's canvas can mount. */
  graph?: { nodes: Array<{ id: string; name: string }>; edges: Array<[string, string]> };
  /** Health notes the fixture wants to prove on the board (W2 §B.1). */
  boardHealth?: { level: "ok" | "warn" | "error"; note: string | null };
}

const RUN_SEEDS: RunSeed[] = [
  {
    id: "run-live",
    name: "Morning brief",
    kind: "flow",
    status: "running",
    createdHoursAgo: 0.2,
    durationMs: 620_000,
    steps: { total: 3, completed: 1, failed: 0 },
    currentNodeId: "rank",
    ideSessionKey: "session-victor",
    breakpoints: ["publish"],
    tokenBudget: 10_000_000,
    budgetAction: "pause",
    graph: {
      nodes: [
        { id: "harvest", name: "Harvest the feeds" },
        { id: "rank", name: "Rank what matters" },
        { id: "publish", name: "Publish the brief" },
      ],
      edges: [
        ["harvest", "rank"],
        ["rank", "publish"],
      ],
    },
  },
  {
    id: "run-approval",
    name: "Morning brief",
    kind: "flow",
    status: "paused",
    createdHoursAgo: 3,
    durationMs: 180_000,
    steps: { total: 3, completed: 2, failed: 0 },
    currentNodeId: "publish",
    awaitingApprovalNodeId: "publish",
    // Paused + a live IDE session ⇒ can.jump is true (status ≠ running) — proves the Jump
    // dialog's node gate (unknown_node) independently of the live-run refusal (run_is_live).
    ideSessionKey: "session-victor",
  },
  {
    id: "run-done",
    name: "Weekly permission audit",
    kind: "flow",
    status: "completed",
    createdHoursAgo: 26,
    durationMs: 44_000,
    steps: { total: 1, completed: 1, failed: 0 },
  },
  {
    id: "run-failed",
    name: "Release notes from the changelog",
    kind: "flow",
    status: "failed",
    createdHoursAgo: 50,
    durationMs: 9_000,
    steps: { total: 2, completed: 1, failed: 1 },
    error: "The changelog source returned 404.",
  },
  {
    // Proves the amber `completed-degraded` status and the named-not-counted degraded bar.
    id: "run-degraded",
    name: "Morning brief",
    kind: "flow",
    status: "completed",
    createdHoursAgo: 8,
    durationMs: 210_000,
    steps: { total: 3, completed: 3, failed: 0 },
    degradedSources: 3,
    degraded: [
      {
        nodeId: "harvest",
        sourceId: "teva-candles",
        kind: "quotes",
        reason: "POST …/quotes.candles returned HTTP 401",
      },
      { nodeId: "harvest", sourceId: "world-events", kind: "feed", reason: "timed out after 8s" },
      {
        nodeId: "rank",
        sourceId: "prior-brief",
        kind: "document",
        reason: "document was archived",
      },
    ],
  },
  {
    // Proves Step is enabled from `can` (live flow run with an IDE session) and Jump refuses
    // a live run (`status != running` is required — see the state machine in §B.3).
    id: "run-stepping",
    name: "Nightly access review",
    kind: "flow",
    status: "running",
    createdHoursAgo: 0.05,
    durationMs: 30_000,
    steps: { total: 1, completed: 0, failed: 0 },
    currentNodeId: "audit",
    ideSessionKey: "session-victor",
  },
  {
    // Proves the back-stack: a child spawned by run-live via a subflow.
    id: "run-child",
    name: "Rank what matters (subflow)",
    kind: "flow",
    status: "completed",
    createdHoursAgo: 0.15,
    durationMs: 40_000,
    steps: { total: 1, completed: 1, failed: 0 },
    causalRunId: "run-live",
  },
  {
    // Proves the board's "orphaned run" health class: an IDE session that stopped checking in.
    id: "run-orphaned",
    name: "Release notes from the changelog",
    kind: "flow",
    status: "running",
    createdHoursAgo: 4,
    durationMs: 900_000,
    steps: { total: 2, completed: 1, failed: 0 },
    currentNodeId: "write",
    ideSessionKey: "session-ghost",
    boardHealth: {
      level: "error",
      note: "the IDE window running this is no longer checked in — the executor is gone",
    },
  },
];

/** `children[]` as `runs.get` carries them — a sibling of the run, not a field on the DTO. */
const CHILD_RUNS: Record<string, Array<{ runId: string; via: "amend" | "subflow" }>> = {
  "run-live": [{ runId: "run-child", via: "subflow" }],
};

/**
 * Board-only spend, keyed by run id. `run-live` carries the exact figures the wave record
 * cites (cacheReadShare 0.967, amplification 142) so the red thresholds are provable offline.
 */
const RUN_SPEND: Record<
  string,
  {
    tokens: number;
    input: number;
    cacheWrite: number;
    cacheRead: number;
    output: number;
    requests: number;
    actions: number;
    amplification: number;
    cacheReadShare: number;
  }
> = {
  "run-live": {
    tokens: 96_700_000,
    input: 1_800_000,
    cacheWrite: 2_100_000,
    cacheRead: 91_500_000,
    output: 1_300_000,
    requests: 84,
    actions: 212,
    amplification: 142,
    cacheReadShare: 0.967,
  },
};

/** One node card per run, for the run viewer's Steps tab (`meta.prompt`/`promptChars`). */
const RUN_STEP_META: Record<
  string,
  Array<{ model: string; totalTokens: number; prompt: string }>
> = {
  "run-live": [
    {
      model: "lclqwen",
      totalTokens: 4_210,
      prompt: "Harvest the three configured feeds and return raw items as JSON.",
    },
    {
      model: "lclgemma",
      totalTokens: 91_500_000,
      prompt: "Rank the harvested items by relevance to the workspace's stated interests.",
    },
    {
      model: "lclgemma",
      totalTokens: 1_100,
      prompt: "Publish the ranked brief. Wait for a human to approve before sending.",
    },
  ],
};

interface ArtifactSeed {
  path: string;
  version: number;
  nodeId: string | null;
  mediaType: string;
  bytes: number;
  hasContent: boolean;
  contentWithheld: boolean;
  sensitive: boolean;
  content: string | null;
}

/** One mirrored, one withheld (stored by reference only), one sensitive (needs `run`, not `read`). */
const RUN_ARTIFACTS: Record<string, ArtifactSeed[]> = {
  "run-live": [
    {
      path: "brief/morning-brief.md",
      version: 1,
      nodeId: "publish",
      mediaType: "text/markdown",
      bytes: 3_240,
      hasContent: true,
      contentWithheld: false,
      sensitive: false,
      content: "# Morning brief\n\nThree feeds in, one table out.",
    },
    {
      path: "brief/raw-harvest.jsonl",
      version: 1,
      nodeId: "harvest",
      mediaType: "application/jsonl",
      bytes: 18_400_000,
      hasContent: false,
      contentWithheld: true,
      sensitive: false,
      content: null,
    },
    {
      path: "brief/source-credentials.json",
      version: 1,
      nodeId: "harvest",
      mediaType: "application/json",
      bytes: 512,
      hasContent: true,
      contentWithheld: false,
      sensitive: true,
      content: '{"apiKey":"•••• (elevated access required)"}',
    },
  ],
};

const EFFICIENCY_NODES: Record<
  string,
  Array<{
    nodeId: string;
    requests: number;
    actions: number;
    filesWritten: number;
    contentTokens: number;
    billedTokens: number;
    cacheReadTokens: number;
    amplification: number | null;
    models: string[];
    effortMax: string | null;
  }>
> = {
  "run-live": [
    {
      nodeId: "harvest",
      requests: 6,
      actions: 9,
      filesWritten: 1,
      contentTokens: 4_210,
      billedTokens: 4_210,
      cacheReadTokens: 0,
      amplification: 1,
      models: ["lclqwen"],
      effortMax: "medium",
    },
    {
      nodeId: "rank",
      requests: 74,
      actions: 190,
      filesWritten: 0,
      contentTokens: 645_070,
      billedTokens: 91_500_000,
      cacheReadTokens: 88_500_000,
      amplification: 142,
      models: ["lclgemma"],
      effortMax: "high",
    },
    {
      nodeId: "publish",
      requests: 4,
      actions: 13,
      filesWritten: 1,
      contentTokens: 1_100,
      billedTokens: 1_100,
      cacheReadTokens: 0,
      amplification: 1,
      models: ["lclgemma"],
      effortMax: "low",
    },
  ],
};

/** ~12 rows, three with `runId:null`, two failed, spanning ≥3 tools and 2 models (D.4). */
function seedLedger(): ActionRow[] {
  const rows: Array<Partial<ActionRow> & { seq: number }> = [
    {
      seq: 1,
      runId: "run-live",
      stepId: "harvest",
      kind: "tool",
      status: "completed",
      toolName: "web.search",
      model: "lclqwen",
      summary: "Searched 3 feeds",
    },
    {
      seq: 2,
      runId: "run-live",
      stepId: "harvest",
      kind: "tool",
      status: "completed",
      toolName: "web.fetch",
      model: "lclqwen",
      summary: "Fetched candles: 401",
    },
    {
      seq: 3,
      runId: "run-live",
      stepId: "rank",
      kind: "tool",
      status: "completed",
      toolName: "Read",
      model: "lclgemma",
      summary: "Read 84 cached items",
    },
    {
      seq: 4,
      runId: "run-live",
      stepId: "rank",
      kind: "tool",
      status: "failed",
      toolName: "Read",
      model: "lclgemma",
      error: "document was archived",
    },
    {
      seq: 5,
      runId: "run-live",
      stepId: "publish",
      kind: "tool",
      status: "completed",
      toolName: "Write",
      model: "lclgemma",
      summary: "Wrote the brief",
    },
    {
      seq: 6,
      runId: "run-done",
      stepId: "audit",
      kind: "tool",
      status: "completed",
      toolName: "Grep",
      model: "lclgemma",
      summary: "Compared grants",
    },
    {
      seq: 7,
      runId: "run-failed",
      stepId: "collect",
      kind: "tool",
      status: "completed",
      toolName: "web.fetch",
      model: "reviewer",
      summary: "Collected merged PRs",
    },
    {
      seq: 8,
      runId: "run-failed",
      stepId: "write",
      kind: "tool",
      status: "failed",
      toolName: "web.fetch",
      model: "reviewer",
      error: "The changelog source returned 404.",
    },
    // Every recorded row from the extension's transcript tailer lands with runId:null (§A.1) —
    // it does not know a locally-executed flow's KnowIt run id.
    {
      seq: 9,
      runId: null,
      kind: "tool",
      status: "completed",
      toolName: "Edit",
      model: "lclqwen",
      summary: "Edited a local file",
    },
    {
      seq: 10,
      runId: null,
      kind: "command",
      status: "completed",
      toolName: "Bash",
      model: "lclqwen",
      summary: "Ran the test suite",
    },
    {
      seq: 11,
      runId: null,
      kind: "tool",
      status: "completed",
      toolName: "Write",
      model: "lclgemma",
      summary: "Wrote a scratch note",
    },
    {
      seq: 12,
      runId: "run-degraded",
      stepId: "harvest",
      kind: "tool",
      status: "completed",
      toolName: "web.fetch",
      model: "lclqwen",
      summary: "Quotes source refused (401)",
    },
  ];
  return rows.map((row) => ({
    id: `action-${row.seq}`,
    runId: row.runId ?? null,
    stepId: row.stepId ?? null,
    seq: row.seq,
    kind: row.kind ?? "tool",
    status: row.status ?? "completed",
    summary: row.summary ?? null,
    summaryGeneric: false,
    toolName: row.toolName ?? null,
    toolInput: null,
    resultBytes: row.status === "failed" ? null : 1200 + row.seq * 40,
    resultDigest: null,
    model: row.model ?? null,
    effort: "medium",
    thinkerId: row.model ?? null,
    thinker: row.model ?? null,
    tokens: {
      input: 400 + row.seq * 10,
      cacheWrite: 0,
      cacheRead: row.seq === 3 ? 88_500_000 : 0,
      output: 120 + row.seq * 5,
      total: (row.seq === 3 ? 88_500_000 : 0) + 520 + row.seq * 15,
    },
    costMicros: null,
    actorUserId: "user-1",
    requestId: `req-${row.seq}`,
    sessionKey: row.runId ? `session-${row.runId}` : "session-victor",
    startedAt: hoursAgo(1 + row.seq * 0.1),
    endedAt: hoursAgo(1 + row.seq * 0.1 - 0.01),
    durationMs: 400 + row.seq * 60,
    error: row.error ?? null,
  }));
}

interface DirectiveSeed {
  id: string;
  flowId: string;
  text: string;
  active: boolean;
  createdBy: string;
  createdHoursAgo: number;
  revokedHoursAgo?: number;
}

const DIRECTIVE_SEEDS: DirectiveSeed[] = [
  {
    id: "dir-1",
    flowId: "morning-brief",
    text: "Prefer sources published in the last 24h.",
    active: true,
    createdBy: "Ada Lovelace",
    createdHoursAgo: 5,
  },
  {
    id: "dir-2",
    flowId: "morning-brief",
    text: "Keep the table under ten rows.",
    active: true,
    createdBy: "Ada Lovelace",
    createdHoursAgo: 2,
  },
  {
    id: "dir-3",
    flowId: "morning-brief",
    text: "Skip the finance section this week.",
    active: false,
    createdBy: "Grace Hopper",
    createdHoursAgo: 30,
    revokedHoursAgo: 20,
  },
];

/**
 * The connected editor windows every IDE surface reads — the Task Center's target picker, the
 * W4 console, the documentation-sync windows band. ONE fixture set, exported so `fakeIde.ts`
 * enforces against the same rows the picker offers: a session that is askable here and
 * refused there would be a fixture bug pretending to be a permission bug.
 */
export const IDE_SESSION_SEEDS: IdeSession[] = [
  {
    id: "ide-1",
    sessionKey: "session-victor",
    userId: "user-1",
    userName: "Ada Lovelace",
    isOwn: true,
    machineName: "ada-macbook",
    workspaceName: "knowIt",
    workspacePath: "/Users/ada/knowIt",
    extensionVersion: "0.5.57",
    bridgeVersion: "0.5.57",
    machineId: "machine-1",
    boundTeamId: "ws-acme",
    bindingMismatch: false,
    // W4: `ide.inbound.ask` is what makes the policy badge readable rather than "unknown",
    // and `ide.grants.v1` is what stops the grant gate refusing this window
    // `receiver-unsupported`. Both are declared capabilities, never inferred.
    capabilities: [
      "http",
      "flow.run",
      "flow.step",
      "ide.ask",
      "ide.windows",
      "ide.open",
      "ide.inbound.ask",
      "ide.binding.v1",
      "ide.grants.v1",
    ],
    startedAt: hoursAgo(6),
    lastSeenAt: hoursAgo(0.02),
    revokedAt: null,
    isOnline: true,
  },
  {
    id: "ide-2",
    sessionKey: "session-grace",
    userId: "user-2",
    userName: "Grace Hopper",
    isOwn: false,
    machineName: "grace-thinkpad",
    workspaceName: "knowIt",
    workspacePath: "/home/grace/knowIt",
    extensionVersion: "0.5.55",
    bridgeVersion: "0.5.55",
    machineId: "machine-2",
    boundTeamId: "ws-acme",
    bindingMismatch: false,
    capabilities: ["flow.run"],
    startedAt: hoursAgo(20),
    lastSeenAt: hoursAgo(0.5),
    revokedAt: null,
    isOnline: true,
  },
  {
    id: "ide-3",
    sessionKey: "session-ghost",
    userId: "user-1",
    userName: "Ada Lovelace",
    isOwn: true,
    machineName: "ada-old-laptop",
    workspaceName: "knowIt",
    workspacePath: "/Users/ada/knowIt-old",
    extensionVersion: "0.5.40",
    bridgeVersion: "0.5.40",
    machineId: "machine-3",
    // W4: bound to ANOTHER workspace — the console's warning chip, and the reason a clone's
    // sync goes quiet with no error anywhere. Only the first 8 characters are ever rendered:
    // resolving a foreign teamId to a name would be a cross-tenant leak.
    boundTeamId: "ws-elsewhere",
    bindingMismatch: true,
    // `allow` is the YELLOW policy: prompts from other windows run here unattended.
    capabilities: ["http", "ide.inbound.allow"],
    startedAt: hoursAgo(48),
    lastSeenAt: hoursAgo(4),
    revokedAt: null,
    isOnline: false,
  },
];

interface DispatchSeed extends Dispatch {
  /** Board-only: Copilot has no transcript, so its tokens are excluded, never reported as zero. */
  telemetry?: "full" | "partial";
  budget?: number;
}

const DISPATCH_SEEDS: DispatchSeed[] = [
  {
    id: "dispatch-open",
    kind: "flow",
    agent: "Copilot",
    status: "queued",
    target: "workspace window",
    claimedBy: null,
    runId: null,
    createdAt: hoursAgo(0.1),
    claimedAt: null,
    expiresAt: hoursAhead(0.2),
    completedAt: null,
    result: null,
    error: null,
    redacted: false,
    originSessionKey: null,
    conversationId: null,
    tokenBudget: 500_000,
    payload: JSON.stringify({ prompt: "Draft the release notes for 0.6.4" }),
    isOwn: true,
    statusDetail: "queued",
    queuedBehind: 2,
    ownerUserName: "Ada Lovelace",
    run: null,
    telemetry: "partial",
    budget: 500_000,
  },
  {
    id: "dispatch-done",
    kind: "thought",
    agent: "ClaudeCode",
    status: "completed",
    target: "workspace window",
    claimedBy: "workspace window",
    runId: "run-done",
    createdAt: hoursAgo(5),
    claimedAt: hoursAgo(4.9),
    expiresAt: hoursAgo(4.7),
    completedAt: hoursAgo(4.8),
    result: "Answered in 4 seconds.",
    error: null,
    redacted: false,
    originSessionKey: null,
    conversationId: "conv-remote-1",
    tokenBudget: null,
    payload: JSON.stringify({ prompt: "Summarise what changed in the sync layer" }),
    isOwn: true,
    statusDetail: "completed",
    queuedBehind: 0,
    ownerUserName: "Ada Lovelace",
    // The parked flow. WITHOUT this row the approval card in Remote Sessions is untestable —
    // it is raised by `runId && run.awaitingApprovalNodeId`, and answered with `runs.approve`.
    run: { status: "paused", currentNodeId: "publish", awaitingApprovalNodeId: "publish" },
    telemetry: "full",
  },
  {
    // Somebody else's: visible as activity, unreadable as content.
    id: "dispatch-other",
    kind: "thought",
    agent: "ClaudeCode",
    status: "running",
    target: "another window",
    claimedBy: "another window",
    runId: null,
    createdAt: hoursAgo(0.4),
    claimedAt: hoursAgo(0.3),
    expiresAt: hoursAhead(0.1),
    completedAt: null,
    result: null,
    error: null,
    redacted: true,
    originSessionKey: null,
    conversationId: null,
    tokenBudget: null,
    // Redacted: the server nulls the payload for somebody else's row, and so does this.
    payload: null,
    isOwn: false,
    statusDetail: "running",
    queuedBehind: 0,
    ownerUserName: "Grace Hopper",
    run: null,
    telemetry: "full",
  },
];

const TERMINAL_STATUSES = new Set(["completed", "completed-degraded", "failed", "cancelled"]);

/**
 * What the platform computes as `can` (§B.3's state machine), modelled rather than copied: a
 * verb is offered when it has somewhere to land, which depends on the run's state — never on
 * the viewer's role. `SupportsControl` = a pulse/task kind, or a flow run with a live IDE
 * session; `SupportsStepping` = a flow run with a live IDE session, specifically.
 */
function abilitiesFor(seed: RunSeed): Abilities {
  const terminal = TERMINAL_STATUSES.has(seed.status);
  const supportsControl =
    ["pulse-advisor", "pulse-backtest", "pulse-strategy-backtest", "task"].includes(seed.kind) ||
    (seed.kind === "flow" && Boolean(seed.ideSessionKey));
  const supportsStepping = seed.kind === "flow" && Boolean(seed.ideSessionKey);
  const canRetry = ["pulse-advisor", "pulse-backtest", "pulse-strategy-backtest"].includes(
    seed.kind,
  );

  const abilities: Abilities = {};
  if (supportsControl && (seed.status === "running" || seed.status === "observing")) {
    abilities[ABILITY.runPause] = true;
  }
  if (supportsControl && seed.status === "paused") {
    abilities[ABILITY.runResume] = true;
  }
  if (supportsControl && !terminal) {
    abilities[ABILITY.runCancel] = true;
  }
  if (canRetry && terminal) {
    abilities[ABILITY.runRetry] = true;
  }
  if (supportsStepping && !terminal) {
    abilities[ABILITY.runStep] = true;
  }
  if (supportsControl && !terminal && seed.status !== "running") {
    abilities[ABILITY.runJump] = true;
  }
  if (seed.awaitingApprovalNodeId) {
    abilities[ABILITY.runApprove] = true;
  }
  return abilities;
}

const EVENT_NAMES = [
  "documents.publish",
  "documents.update",
  "collections.create",
  "users.invite",
  "shares.create",
  "documents.archive",
  "apiKeys.create",
  "groups.update",
];

function seedEvents(): ActivityEvent[] {
  return Array.from({ length: 32 }, (_, index) => ({
    id: `event-${index}`,
    name: EVENT_NAMES[index % EVENT_NAMES.length] ?? "documents.update",
    actor:
      index % 3 === 0
        ? { id: "user-2", name: "Grace Hopper", avatarUrl: null }
        : { id: "user-1", name: "Ada Lovelace", avatarUrl: null },
    createdAt: hoursAgo(index * 2 + 1),
    documentId: index % 2 === 0 ? "doc-1" : null,
    collectionId: index % 4 === 0 ? "col-product" : null,
  }));
}

export interface FakeAutomationDeps {
  /** Throws UnauthorizedError when signed out — the same guard every other fixture call uses. */
  requireSession(): unknown;
  /** The current workspace's server-supplied policy. */
  abilities(): Abilities;
  delay<T>(value: T): Promise<T>;
}

/** The six surfaces the server recognises — anything else is a 200 refusal, not an error. */
const ROUTE_SURFACES: readonly RouteSurface[] = [
  "tasks",
  "flows",
  "app",
  "document",
  "collection",
  "search",
];

/**
 * Navigate fixtures — REAL documents and collections from the fixture workspace, so following a
 * proposal lands on something that exists rather than a plausible-looking 404. (The research
 * fixture has no corpus behind it — the fake research API refuses — so following it lands on the
 * research scene's own "needs the real API" state, which is still the right ROUTE.)
 *
 * "quarterly" matches ONE (⇒ 0.85, auto-navigates; 0.95 needs the exact name); "runbook" matches TWO (⇒ 0.55 with
 * alternates, never auto-navigates). That is the server's deterministic ladder — a single fuzzy
 * match goes straight there and an ambiguous one always asks — made reachable offline. S1 adds
 * the kind: "the research about math" ranks the research row first and counts only research
 * rows on the ladder, exactly as `NavigateTargetRanking.Confidence` does.
 *
 * S4 adds work items: a tile's title is "KEY · <item title>" and its path `/work/{KEY}`, so
 * "open GAME-3" and "the ticket about loader" both land on the same row. Two work rows share
 * "crash" so the work end of the ladder's ambiguous rung ("Open this work item?" with an
 * alternate) is reachable offline too; "loader" and "login" each match ONE.
 */
const NAVIGATE_FIXTURES: readonly NavigateTarget[] = [
  { id: "doc-1", title: "Quarterly plan", path: "/documents/doc-1", kind: "document" },
  { id: "doc-5", title: "Runbook: deploys", path: "/documents/doc-5", kind: "document" },
  { id: "doc-11", title: "Old roadmap (2025)", path: "/documents/doc-11", kind: "document" },
  { id: "doc-12", title: "Deprecated runbook", path: "/documents/doc-12", kind: "document" },
  {
    id: "research-math",
    title: "Math proofs research",
    path: "/research?research=research-math",
    kind: "research",
  },
  {
    id: "col-engineering",
    title: "Engineering",
    path: "/collections/col-engineering",
    kind: "collection",
  },
  {
    id: "work-game-3",
    title: "GAME-3 · Fix the loader crash",
    path: "/work/GAME-3",
    kind: "work",
  },
  {
    id: "work-game-7",
    title: "GAME-7 · Fix the login crash",
    path: "/work/GAME-7",
    kind: "work",
  },
];

/**
 * The server's kind words, as `NavigateTargetRanking.NeedleFromPrompt` reads them. "work item"
 * is listed before "item" so the two-word form wins the alternation.
 */
const NAVIGATE_KIND_WORDS: Record<string, NavigateTarget["kind"]> = {
  research: "research",
  experiment: "research",
  study: "research",
  doc: "document",
  document: "document",
  collection: "collection",
  folder: "collection",
  "work item": "work",
  task: "work",
  ticket: "work",
  tile: "work",
  item: "work",
  issue: "work",
  bug: "work",
  story: "work",
  epic: "work",
};

const NAVIGATE_KIND_PATTERN = new RegExp(
  `^(?:the|a|my)?\\s*(${Object.keys(NAVIGATE_KIND_WORDS).join("|")})\\b\\s*(?:about|on|called|named|for)?\\s*`,
  "i",
);

/**
 * The server's `BuildRoutePrompt` asks the model for `targetKind` from the phrasing; offline,
 * the phrasing is read directly. Returns the kind the words asked for and the words that are
 * left — "open the research about math" → research, "math"; "find the ticket about loader" →
 * work, "loader".
 */
function navigateNeedle(q: string): { kind: NavigateTarget["kind"] | null; needle: string } {
  const stripped = q.replace(
    /^.*?\b(go to|switch to|take me to|open|show( me)?|find|where is)\s+/,
    "",
  );
  const kinded = NAVIGATE_KIND_PATTERN.exec(stripped);
  const kindWord = kinded?.[1]?.toLowerCase();
  const kind: NavigateTarget["kind"] | null =
    kindWord !== undefined ? (NAVIGATE_KIND_WORDS[kindWord] ?? null) : null;
  const needle = (kinded ? stripped.slice(kinded[0].length) : stripped).trim();
  return { kind, needle };
}

function routeCommon(prompt: string, surface: RouteSurface, confidence: number) {
  return {
    intent: prompt,
    confidence,
    alternates: [] as NavigateTarget[],
    surface,
    summary: prompt,
    title: prompt.slice(0, 60),
    missing: [] as string[],
    message: null,
  };
}

export function createFakeAutomation(deps: FakeAutomationDeps) {
  // The spread is SHALLOW, and W3 gave the seeds a nested `definition` the studio mutates on
  // every save — so each FakeApi instance gets its own deep copy, or one test's edits become
  // the next test's starting state.
  let flows: FlowSeed[] = FLOW_SEEDS.map((seed) => ({
    ...seed,
    definition: structuredClone(seed.definition ?? plainDefinition(seed)),
  }));
  const runs: RunSeed[] = RUN_SEEDS.map((seed) => ({ ...seed }));
  let tasks: ScheduledTask[] = [
    {
      id: "task-brief",
      title: "Morning brief, every weekday",
      taskType: "ide-flow",
      flowId: "morning-brief",
      triggerKind: "every",
      recurrence: "1440m",
      onEvent: null,
      paused: false,
      nextFireAt: hoursAhead(14),
      lastFiredAt: hoursAgo(10),
      abilities: {},
    },
    {
      id: "task-audit",
      title: "Weekly permission audit",
      taskType: "ide-flow",
      flowId: "weekly-audit",
      triggerKind: "every",
      recurrence: "10080m",
      onEvent: null,
      paused: true,
      nextFireAt: null,
      lastFiredAt: hoursAgo(150),
      abilities: {},
    },
  ];
  const events = seedEvents();
  let directives: DirectiveSeed[] = DIRECTIVE_SEEDS.map((seed) => ({ ...seed }));
  // Deep enough: a shallow spread would share the nested `run` object between FakeApi
  // instances, and one test answering an approval would change another test's fixture.
  const dispatchSeeds: DispatchSeed[] = DISPATCH_SEEDS.map((seed) => ({
    ...seed,
    run: seed.run ? { ...seed.run } : null,
  }));
  const ledgerSeeds: ActionRow[] = seedLedger();
  // The board's global infra flag: worker stopped (§B.1) — task-brief (active) reads as
  // `error`, task-audit (paused, fired within 7 days) reads as `ok`, proving both classes.
  const workerProcessingJobs = false;

  const toDirective = (seed: DirectiveSeed): Directive => ({
    id: seed.id,
    flowId: seed.flowId,
    text: seed.text,
    createdBy: seed.createdBy,
    active: seed.active,
    createdAt: hoursAgo(seed.createdHoursAgo),
    revokedAt: seed.revokedHoursAgo !== undefined ? hoursAgo(seed.revokedHoursAgo) : null,
    revokedBy: seed.revokedHoursAgo !== undefined ? "You" : null,
  });

  const manage = (): boolean => deps.abilities()[ABILITY.workspaceUpdate] === true;

  const requireManage = (message: string): void => {
    if (!manage()) {
      throw new ForbiddenError(message);
    }
  };

  const flowAbilities = (): Abilities =>
    manage()
      ? { [ABILITY.flowRun]: true, [ABILITY.flowUpdate]: true, [ABILITY.flowDelete]: true }
      : {};

  const toFlow = (seed: FlowSeed): Flow => ({
    id: `flow-${seed.slug}`,
    slug: seed.slug,
    name: seed.name,
    status: seed.status,
    kind: "LongRunning",
    versionCount: seed.versions,
    nodeCount: seed.nodes.length,
    documentId: seed.documentId ?? null,
    updatedAt: hoursAgo(seed.updated),
    abilities: flowAbilities(),
    entityLinkKind: seed.entityLinkKind ?? "workspace",
    entityId: seed.entityId ?? null,
    categories: seed.categories ? [...seed.categories] : [],
  });

  const toFlowDetail = (seed: FlowSeed): FlowDetail => ({
    slug: seed.slug,
    name: seed.name,
    description: seed.description,
    status: seed.status,
    kind: "LongRunning",
    version: seed.versions,
    versionCount: seed.versions,
    nodes: seed.nodes.map((node) => ({
      id: node.id,
      name: node.name,
      model: node.model,
      effort: node.model ? "medium" : null,
      purpose: null,
      requireApproval: node.requireApproval === true,
      tokens: 240,
    })),
    // A STRING, exactly as the wire carries it — so a caller that forgets to parse fails here
    // the same way it would against the real server.
    definitionJson: JSON.stringify(seed.definition ?? {}),
    documentId: seed.documentId ?? null,
  });

  const findFlow = (slug: string): FlowSeed => {
    const found = flows.find((flow) => flow.slug === slug);
    if (!found) {
      throw new NotFoundError("No such flow");
    }
    return found;
  };

  const toRun = (seed: RunSeed): Run => ({
    id: seed.id,
    name: seed.name,
    kind: seed.kind,
    // Composed, never stored: precedence is failed > completed-degraded > completed (§B.3).
    status:
      seed.status === "completed" && seed.degradedSources ? "completed-degraded" : seed.status,
    createdAt: hoursAgo(seed.createdHoursAgo),
    startedAt: hoursAgo(seed.createdHoursAgo),
    finishedAt: TERMINAL_STATUSES.has(seed.status) ? hoursAgo(seed.createdHoursAgo - 0.1) : null,
    durationMs: seed.durationMs,
    currentNodeId: seed.currentNodeId ?? null,
    result: seed.status === "completed" ? "Published to Product." : null,
    error: seed.error ?? null,
    steps: { ...seed.steps },
    awaitingApprovalNodeId: seed.awaitingApprovalNodeId ?? null,
    degradedSources: seed.degradedSources ?? 0,
    degraded: seed.degraded ? seed.degraded.map((entry) => ({ ...entry })) : [],
    causalRunId: seed.causalRunId ?? null,
    abilities: abilitiesFor(seed),
  });

  const findRun = (id: string): RunSeed => {
    const found = runs.find((run) => run.id === id);
    if (!found) {
      throw new NotFoundError("No such run");
    }
    return found;
  };

  const findTask = (id: string): ScheduledTask => {
    const found = tasks.find((task) => task.id === id);
    if (!found) {
      throw new NotFoundError("No such task");
    }
    return found;
  };

  return {
    agents: {
      list: async (): Promise<Agent[]> => {
        deps.requireSession();
        return deps.delay(AGENTS.map((agent) => ({ ...agent })));
      },
    },

    flows: {
      list: async (
        options: { includeArchived?: boolean; entity?: { kind: string; id: string } } = {},
      ): Promise<FlowList> => {
        deps.requireSession();
        const visible = flows.filter(
          (flow) => options.includeArchived === true || flow.status !== "archived",
        );
        // R4 — the fixture honours the entity filter too, so the panel is provable offline.
        const matching = options.entity
          ? visible.filter(
              (flow) =>
                (flow.entityLinkKind ?? "workspace") === options.entity?.kind &&
                (flow.entityId ?? null) === options.entity?.id,
            )
          : visible;
        return deps.delay({
          flows: matching.map(toFlow),
          activeCount: matching.filter((flow) => flow.status === "active").length,
          activeLimit: 4,
        });
      },

      classify: async (slug: string, purpose: "reusable" | "task-specific" | "unclassified") => {
        deps.requireSession();
        requireManage("You cannot change flows in this workspace");
        const flow = findFlow(slug);
        flow.categories = (flow.categories ?? []).filter(
          (x) => x !== "reusable" && x !== "task-specific",
        );
        if (purpose !== "unclassified") flow.categories.push(purpose);
        return deps.delay({ categories: [...flow.categories] });
      },

      get: async (slug: string): Promise<FlowDetail> => {
        deps.requireSession();
        return deps.delay(toFlowDetail(findFlow(slug)));
      },

      definition: async (slug: string, version?: number): Promise<FlowDefinitionResult> => {
        deps.requireSession();
        const seed = findFlow(slug);
        const flow = toFlowDetail(seed);
        // A historical read is what Restore is built on, so the fake must answer one — it
        // returns today's definition tagged with the older version number, which is enough to
        // exercise "get version N, save it as the new head" end to end.
        return deps.delay({
          flow: version === undefined ? flow : { ...flow, version },
          definition: structuredClone(seed.definition ?? {}),
        });
      },

      /**
       * The fake VALIDATES, because a gate that only exists on the server is a gate slate's
       * tests cannot prove. Two rules only: the ones `AiFlowStore.Validate` refuses on and that
       * an editor can trip in one click — no start node, no nodes. The full parity list lives
       * in the package's `diagnostics()`, which the studio runs before it ever gets here.
       */
      save: async (input: {
        slug: string;
        definition: unknown;
        note?: string;
      }): Promise<FlowSaveResult> => {
        deps.requireSession();
        requireManage("You cannot change flows in this workspace");
        const definition = (input.definition ?? {}) as Record<string, unknown>;
        const nodes = Array.isArray(definition.nodes) ? definition.nodes : [];
        const nodeIds = new Set(
          nodes.map((node) => String((node as { nodeId?: unknown }).nodeId ?? "")),
        );
        const errors: string[] = [];
        if (nodes.length === 0) {
          errors.push("A flow needs at least one node.");
        }
        const start = String(definition.startNodeId ?? "");
        if (start === "") {
          errors.push("A flow needs a start node.");
        } else if (nodes.length > 0 && !nodeIds.has(start)) {
          errors.push(`Start node '${start}' is not one of this flow's nodes.`);
        }
        if (errors.length > 0) {
          return deps.delay({
            ok: false,
            error: "invalid_flow",
            version: null,
            versionId: null,
            unchanged: false,
            warnings: [],
            errors,
          });
        }

        const seed = flows.find((flow) => flow.slug === input.slug);
        const serialized = JSON.stringify(definition);
        if (seed && JSON.stringify(seed.definition ?? {}) === serialized) {
          // Re-saving an identical definition mints nothing — the server hashes it and answers
          // with the head. Saying "saved as version 5" when nothing moved is a small lie the
          // version list would then have to keep.
          return deps.delay({
            ok: true,
            error: null,
            version: seed.versions,
            versionId: `ver-${seed.slug}-${seed.versions}`,
            unchanged: true,
            warnings: [],
            errors: [],
          });
        }

        if (seed) {
          seed.definition = structuredClone(definition);
          seed.name = String(definition.name ?? seed.name);
          seed.description = String(definition.description ?? seed.description);
          seed.versions += 1;
          seed.updated = 0;
          seed.nodes = nodes.map((node) => {
            const entry = node as Record<string, unknown>;
            return {
              id: String(entry.nodeId ?? ""),
              name: String(entry.name ?? entry.nodeId ?? ""),
              model: typeof entry.model === "string" ? entry.model : null,
              requireApproval: entry.requireApproval === true,
            };
          });
          return deps.delay({
            ok: true,
            error: null,
            version: seed.versions,
            versionId: `ver-${seed.slug}-${seed.versions}`,
            unchanged: false,
            warnings: [],
            errors: [],
          });
        }

        // A save on a free slug is how a flow is CREATED (§A.4) — there is no `ai-flows.create`.
        const created: FlowSeed = {
          slug: input.slug,
          name: String(definition.name ?? input.slug),
          description: String(definition.description ?? ""),
          status: "draft",
          nodes: nodes.map((node) => {
            const entry = node as Record<string, unknown>;
            return {
              id: String(entry.nodeId ?? ""),
              name: String(entry.name ?? entry.nodeId ?? ""),
              model: typeof entry.model === "string" ? entry.model : null,
              requireApproval: entry.requireApproval === true,
            };
          }),
          versions: 1,
          updated: 0,
          definition: structuredClone(definition),
        };
        flows = [created, ...flows];
        return deps.delay({
          ok: true,
          error: null,
          version: 1,
          versionId: `ver-${created.slug}-1`,
          unchanged: false,
          warnings: [],
          errors: [],
        });
      },

      attachDoc: async (input: {
        slug: string;
        documentId?: string;
        collectionId?: string;
      }): Promise<FlowDocAttachment> => {
        deps.requireSession();
        // The server gates this on nothing but a session (W3 §F-3 — any member can rebind any
        // flow's documentation). Slate gates it client-side, so the fake must too, or the
        // surface's honesty is untested.
        requireManage("You cannot change flows in this workspace");
        const seed = findFlow(input.slug);
        const created = !input.documentId;
        seed.documentId = input.documentId ?? `doc-flow-${seed.slug}`;
        return deps.delay({
          documentId: seed.documentId,
          created,
          title: created ? `${seed.name} — how it works` : null,
          urlId: created ? `flow-${seed.slug}` : null,
        });
      },

      nodeHistory: async (input: {
        slug: string;
        nodeId: string;
        limit?: number;
      }): Promise<FlowNodeHistory> => {
        deps.requireSession();
        const seed = findFlow(input.slug);
        const known = seed.nodes.some((node) => node.id === input.nodeId);
        if (!known) {
          return deps.delay({
            nodeId: input.nodeId,
            slug: input.slug,
            matchedBy: "none",
            matchNote: "",
            runs: [],
          });
        }
        const limit = Math.min(20, Math.max(1, input.limit ?? 3));
        return deps.delay({
          nodeId: input.nodeId,
          slug: input.slug,
          matchedBy: "flow+nodeId",
          matchNote: "",
          runs: Array.from({ length: Math.min(limit, 2) }, (_, index) => ({
            runId: index === 0 ? "run-live" : "run-done",
            runName: seed.name,
            runStatus: index === 0 ? "running" : "completed",
            stepStatus: index === 0 ? "running" : "completed",
            startedAt: hoursAgo(index + 1),
            endedAt: index === 0 ? null : hoursAgo(index),
            durationMs: index === 0 ? null : 42_000,
            error: null,
            actions: 6,
            tokens: {
              input: 1_200,
              cacheWrite: 300,
              cacheRead: 9_400,
              output: 800,
              total: 11_700,
            },
            contentTokens: 240,
            // Deliberately over the thresholds the Node tab paints red: a fixture that never
            // trips the alarm never proves the alarm works.
            amplification: 48.75,
            cacheReadShare: 0.94,
          })),
        });
      },

      versions: async (slug: string): Promise<FlowVersion[]> => {
        deps.requireSession();
        const seed = findFlow(slug);
        return deps.delay(
          Array.from({ length: seed.versions }, (_, index) => {
            const version = seed.versions - index;
            return {
              version,
              createdAt: hoursAgo(seed.updated + index * 24),
              createdBy: "ada@example.com",
              note: version === 1 ? "first version" : "edited a node",
              isHead: index === 0,
            };
          }),
        );
      },

      setStatus: async (slug: string, status: FlowStatus): Promise<Flow> => {
        deps.requireSession();
        requireManage("You cannot change flows in this workspace");
        const seed = findFlow(slug);
        seed.status = status;
        return deps.delay(toFlow(seed));
      },

      run: async (slug: string, request: FlowRunRequest = {}): Promise<FlowRunAck> => {
        deps.requireSession();
        requireManage("You cannot run flows in this workspace");
        findFlow(slug);
        return deps.delay({
          host: request.host === "ide" ? "ide" : "server",
          runId: request.host === "ide" ? null : "run-live",
          dispatchId: request.host === "ide" ? "dispatch-new" : null,
          target: request.host === "ide" ? "workspace window" : null,
          note:
            request.host === "ide"
              ? "queued for an editor window to claim"
              : "launched on the server host",
        });
      },

      delete: async (slug: string): Promise<void> => {
        deps.requireSession();
        requireManage("You cannot delete flows in this workspace");
        const seed = findFlow(slug);
        if (seed.status !== "archived") {
          // The platform's rule, not a UI nicety: archive is reversible, delete is not.
          throw new ForbiddenError("Archive the flow before deleting it");
        }
        flows = flows.filter((flow) => flow.slug !== slug);
        return deps.delay(undefined);
      },
    },

    /**
     * The machinery board: one snapshot unifying scheduled work, live runs and dispatches,
     * each row carrying its own health rollup. Board rows carry no policy (D.2 rule 6); the
     * gate lives on the workspace policy, exercised by the same rows the UI reads.
     */
    board: {
      snapshot: async (
        query: { activeOnly?: boolean; limit?: number } = {},
      ): Promise<TaskBoard> => {
        deps.requireSession();
        const active = new Set(["requested", "running", "observing", "paused"]);

        const scheduledRows: TaskRow[] = tasks.map((task) => {
          const overdue = task.nextFireAt
            ? Date.parse(task.nextFireAt) < BASE_TIME - 5 * 60_000
            : false;
          const health: TaskRow["health"] =
            !task.paused && workerProcessingJobs === false
              ? { level: "error", note: "worker stopped — this task cannot fire" }
              : !task.paused && overdue
                ? { level: "warn", note: `overdue — was due ${task.nextFireAt}` }
                : task.paused &&
                    task.lastFiredAt &&
                    Date.parse(task.lastFiredAt) < BASE_TIME - 7 * 86_400_000
                  ? { level: "warn", note: "paused for more than 7 days" }
                  : { level: "ok", note: null };
          return {
            id: task.id,
            source: "scheduled",
            title: task.title,
            kind: "recurring",
            state: task.paused ? "paused" : "active",
            schedule: {
              nextFireAt: task.nextFireAt,
              lastFiredAt: task.lastFiredAt,
              expiresAt: null,
              recurrence: task.recurrence,
              triggerKind: task.triggerKind,
            },
            progress: null,
            spend: null,
            target: null,
            health,
            links: { decisionId: task.id, flowId: task.flowId },
            lastActionAt: task.lastFiredAt,
          };
        });

        const runRows: TaskRow[] = runs.map((seed) => {
          const spend = RUN_SPEND[seed.id];
          const health: TaskRow["health"] = seed.boardHealth ?? { level: "ok", note: null };
          return {
            id: seed.id,
            source: "run",
            title: seed.name,
            kind: seed.kind,
            state: seed.status,
            schedule: null,
            progress: {
              nodesDone: seed.steps.completed,
              nodesTotal: seed.steps.total,
              cursorNode: seed.currentNodeId ?? null,
            },
            spend: spend
              ? {
                  tokens: spend.tokens,
                  input: spend.input,
                  cacheWrite: spend.cacheWrite,
                  cacheRead: spend.cacheRead,
                  output: spend.output,
                  budget: seed.tokenBudget ?? null,
                  requests: spend.requests,
                  actions: spend.actions,
                  amplification: spend.amplification,
                  cacheReadShare: spend.cacheReadShare,
                }
              : seed.tokenBudget
                ? {
                    tokens: null,
                    input: null,
                    cacheWrite: null,
                    cacheRead: null,
                    output: null,
                    budget: seed.tokenBudget,
                    requests: null,
                    actions: null,
                    amplification: null,
                    cacheReadShare: null,
                  }
                : null,
            target: seed.ideSessionKey
              ? {
                  ideSessionKey: seed.ideSessionKey,
                  machine: null,
                  workspace: "knowIt",
                  agent: "ClaudeCode",
                  telemetry: "full",
                  executorPid: 4242,
                  canStep: abilitiesFor(seed)[ABILITY.runStep] === true,
                  stepMode: false,
                  ideLive: !seed.boardHealth,
                }
              : null,
            health,
            links: { run: seed.id, decisionId: null, flowSlug: null, flowVersion: null },
            lastActionAt: hoursAgo(seed.createdHoursAgo),
          };
        });

        const dispatchRows: TaskRow[] = dispatchSeeds.map((entry) => ({
          id: entry.id,
          source: "dispatch",
          title: `${entry.kind} → ${entry.agent}`,
          kind: "short-running",
          state: entry.status,
          schedule: {
            nextFireAt: null,
            lastFiredAt: null,
            expiresAt: entry.expiresAt,
            recurrence: null,
            triggerKind: null,
          },
          progress: null,
          spend: entry.budget
            ? {
                tokens: null,
                input: null,
                cacheWrite: null,
                cacheRead: null,
                output: null,
                budget: entry.budget,
                requests: null,
                actions: null,
                amplification: null,
                cacheReadShare: null,
              }
            : null,
          target: {
            ideSessionKey: null,
            machine: null,
            workspace: entry.target,
            agent: entry.agent,
            telemetry: entry.telemetry ?? "full",
            executorPid: null,
            canStep: false,
            stepMode: false,
            ideLive: entry.status === "queued" || entry.status === "running",
          },
          health:
            entry.status === "queued" && !IDE_SESSION_SEEDS.some((s) => s.isOnline)
              ? {
                  level: "error",
                  note: "no live IDE session can claim this — the machine is not checked in",
                }
              : { level: "ok", note: null },
          links: { run: entry.runId },
          lastActionAt: entry.createdAt,
        }));

        let allRows = [...scheduledRows, ...runRows, ...dispatchRows];
        if (query.activeOnly) {
          allRows = allRows.filter(
            (row) =>
              row.state === "active" ||
              active.has(row.state) ||
              row.state === "queued" ||
              row.state === "claimed",
          );
        }
        if (query.limit) {
          allRows = allRows.slice(0, query.limit);
        }

        const worst = allRows.reduce<"ok" | "warn" | "error">((acc, row) => {
          if (row.health.level === "error" || acc === "error")
            return row.health.level === "error" ? "error" : acc;
          if (row.health.level === "warn" || acc === "warn") return "warn";
          return "ok";
        }, "ok");

        return deps.delay({
          tasks: allRows,
          worstHealth: worst,
          warnings: workerProcessingJobs
            ? []
            : [
                "knowit-worker is not processing scheduled jobs — every recurring task below is dead until it is restarted",
              ],
          infrastructure: {
            workerProcessingJobs,
            liveIdeSessions: IDE_SESSION_SEEDS.filter((s) => s.isOnline).length,
          },
          checkedAt: new Date(BASE_TIME).toISOString(),
        });
      },
    },

    /** The command ledger: every AI action the workspace ran, grouped, filtered or per-run. */
    ledger: {
      actions: async (query: LedgerQuery = {}): Promise<LedgerPage> => {
        deps.requireSession();
        const filtered = ledgerSeeds
          .filter((row) => (query.runId !== undefined ? row.runId === query.runId : true))
          .filter((row) => (query.actorUserId ? row.actorUserId === query.actorUserId : true))
          .filter((row) => (query.toolName ? row.toolName === query.toolName : true))
          .filter((row) => (query.kind ? row.kind === query.kind : true))
          .filter((row) => (query.failedOnly ? row.status === "failed" : true));
        const offset = query.offset ?? 0;
        const limit = query.limit ?? 200;
        return deps.delay({
          total: filtered.length,
          limit,
          offset,
          actions: filtered.slice(offset, offset + limit).map((row) => ({ ...row })),
        });
      },

      report: async (query: ReportQuery = {}): Promise<LedgerReport> => {
        deps.requireSession();
        const groupBy = query.groupBy ?? "tool";
        const rows = query.runId
          ? ledgerSeeds.filter((row) => row.runId === query.runId)
          : ledgerSeeds;
        const keyOf = (row: ActionRow): string => {
          switch (groupBy) {
            case "model":
              return row.model ?? "unknown";
            case "effort":
              return row.effort ?? "unknown";
            case "node":
              return row.stepId ?? "unknown";
            case "command_family":
              return (row.toolName ?? "unknown").split(".")[0] ?? "unknown";
            case "thinker":
              return row.thinkerId ?? "unknown";
            case "kind":
              return row.kind;
            case "status":
              return row.status;
            case "actor":
              return row.actorUserId ?? "unknown";
            default:
              return row.toolName ?? "unknown";
          }
        };
        const grouped = new Map<string, ActionRow[]>();
        for (const row of rows) {
          const key = keyOf(row);
          grouped.set(key, [...(grouped.get(key) ?? []), row]);
        }
        const reportRows = Array.from(grouped.entries()).map(([key, group]) => ({
          key,
          calls: group.length,
          inputTokens: group.reduce((sum, r) => sum + r.tokens.input, 0),
          cacheWriteTokens: group.reduce((sum, r) => sum + r.tokens.cacheWrite, 0),
          cacheReadTokens: group.reduce((sum, r) => sum + r.tokens.cacheRead, 0),
          outputTokens: group.reduce((sum, r) => sum + r.tokens.output, 0),
          totalTokens: group.reduce((sum, r) => sum + r.tokens.total, 0),
          resultBytes: group.reduce((sum, r) => sum + (r.resultBytes ?? 0), 0),
          costMicros: null,
        }));
        const totalInput = rows.reduce((sum, r) => sum + r.tokens.input, 0);
        const totalCacheWrite = rows.reduce((sum, r) => sum + r.tokens.cacheWrite, 0);
        const totalCacheRead = rows.reduce((sum, r) => sum + r.tokens.cacheRead, 0);
        const totalOutput = rows.reduce((sum, r) => sum + r.tokens.output, 0);
        const totalAll = rows.reduce((sum, r) => sum + r.tokens.total, 0);
        return deps.delay({
          groupBy,
          rows: reportRows,
          totals: {
            input: totalInput,
            cacheWrite: totalCacheWrite,
            cacheRead: totalCacheRead,
            output: totalOutput,
            total: totalAll,
            contentTokens: rows.reduce((sum, r) => sum + (r.resultBytes ?? 0), 0) / 4,
            amplification: totalOutput > 0 ? totalAll / totalOutput : null,
            cacheReadShare: totalAll > 0 ? totalCacheRead / totalAll : null,
            actions: rows.length,
            requests: rows.length,
            costMicros: null,
          },
        });
      },

      efficiency: async (runId: string): Promise<RunEfficiency> => {
        deps.requireSession();
        const seed = findRun(runId);
        const nodeSeeds = EFFICIENCY_NODES[runId] ?? [
          {
            nodeId: seed.currentNodeId ?? "node-1",
            requests: 1,
            actions: 1,
            filesWritten: 0,
            contentTokens: 100,
            billedTokens: 100,
            cacheReadTokens: 0,
            amplification: 1,
            models: ["lclgemma"],
            effortMax: "low",
          },
        ];
        const nodes = nodeSeeds.map((node, index) => ({
          nodeId: node.nodeId,
          index,
          requests: node.requests,
          actions: node.actions,
          filesWritten: node.filesWritten,
          contentTokens: node.contentTokens,
          billedTokens: node.billedTokens,
          cacheReadTokens: node.cacheReadTokens,
          cacheReadShare:
            node.billedTokens > 0
              ? Number((node.cacheReadTokens / node.billedTokens).toFixed(4))
              : null,
          amplification: node.amplification,
          models: node.models,
          effortMax: node.effortMax,
        }));
        const billedTokens = nodes.reduce((sum, node) => sum + node.billedTokens, 0);
        const contentTokens = nodes.reduce((sum, node) => sum + node.contentTokens, 0);
        const cacheRead = nodes.reduce((sum, node) => sum + node.cacheReadTokens, 0);
        const worst = nodes.reduce<{ id: string; amp: number } | null>((acc, node) => {
          const amp = node.amplification ?? 0;
          return !acc || amp > acc.amp ? { id: node.nodeId, amp } : acc;
        }, null);
        return deps.delay({
          runId,
          nodes,
          run: {
            billedTokens,
            contentTokens,
            amplification:
              contentTokens > 0 ? Number((billedTokens / contentTokens).toFixed(1)) : null,
            requests: nodes.reduce((sum, node) => sum + node.requests, 0),
            actions: nodes.reduce((sum, node) => sum + node.actions, 0),
            tokenBudget: seed.tokenBudget ?? null,
            budgetAction: seed.budgetAction ?? "pause",
            budgetTrippedAt: seed.budgetTrippedAt ?? null,
          },
          worstNode: worst?.id ?? null,
          runCacheReadShare: billedTokens > 0 ? cacheRead / billedTokens : null,
        });
      },

      setBudget: async (
        runId: string,
        tokenBudget: number | null,
        action: "pause" | "warn" = "pause",
      ): Promise<BudgetAck> => {
        deps.requireSession();
        const seed = findRun(runId);
        seed.tokenBudget = tokenBudget;
        seed.budgetAction = action;
        return deps.delay({
          ok: true,
          runId,
          tokenBudget,
          budgetAction: action,
          spent: RUN_SPEND[runId]?.tokens ?? 0,
        });
      },
    },

    runs: {
      list: async (query: RunQuery = {}): Promise<Run[]> => {
        deps.requireSession();
        const active = new Set(["requested", "running", "observing", "paused"]);
        return deps.delay(
          runs
            .filter((run) => (query.status ? run.status === query.status : true))
            .filter((run) => (query.kind ? run.kind === query.kind : true))
            .filter((run) => (query.activeOnly ? active.has(run.status) : true))
            .slice(0, query.limit ?? 50)
            .map(toRun),
        );
      },

      get: async (id: string): Promise<Run> => {
        deps.requireSession();
        return deps.delay(toRun(findRun(id)));
      },

      /** The viewer's shape (D.2 rule 4): the thin `Run` plus graph/children/host/statusReason. */
      detail: async (id: string): Promise<RunDetail> => {
        deps.requireSession();
        const seed = findRun(id);
        const children: ChildRunSummary[] = (CHILD_RUNS[id] ?? []).map((ref) => {
          const child = findRun(ref.runId);
          return {
            runId: child.id,
            name: child.name,
            status: child.status,
            aiFlowSlug: null,
            startedAt: hoursAgo(child.createdHoursAgo),
            endedAt: TERMINAL_STATUSES.has(child.status)
              ? hoursAgo(child.createdHoursAgo - 0.05)
              : null,
            currentNodeId: child.currentNodeId ?? null,
            nodesDone: child.steps.completed,
            nodesTotal: child.steps.total,
            via: ref.via,
          };
        });
        return deps.delay({
          run: toRun(seed),
          host: seed.ideSessionKey ? "ide" : "server",
          statusReason: null,
          graph: seed.graph
            ? {
                nodes: seed.graph.nodes,
                edges: seed.graph.edges.map(([a, b]) => ({ from: a, to: b })),
              }
            : null,
          causalRunId: seed.causalRunId ?? null,
          children,
        });
      },

      /**
       * Weave: the fake serves the shape a run of an ORDINARY flow has — nodes and edges from the
       * seed graph, no tiles. A fixture that invented tiles would let a scene pass here and break
       * against the real server, which is the failure mode fakes exist to prevent.
       */
      tree: async (id: string): Promise<RunTree> => {
        deps.requireSession();
        const seed = findRun(id);
        const nodes = seed.graph?.nodes ?? [];
        const edges = seed.graph?.edges ?? [];
        return deps.delay({
          runId: id,
          flowSlug: null,
          status: seed.status,
          statusReason: null,
          isProject: false,
          reshapeSeq: 0,
          root: null,
          nodes: nodes.map((node, index) => ({
            nodeId: node.id,
            label: node.name ?? node.id,
            kind: "task",
            executor: "thinker",
            ownerRole: null,
            model: null,
            requireApproval: false,
            tile: null,
            runStatus:
              index < seed.steps.completed
                ? "completed"
                : node.id === seed.currentNodeId
                  ? "running"
                  : "pending",
            queuedReason: null,
            branchId: null,
            attempts: index < seed.steps.completed ? 1 : 0,
            questionKey: null,
          })),
          edges: edges.map(([from, to]) => ({ from, to, condition: "always" })),
        });
      },

      steps: async (id: string): Promise<RunStep[]> => {
        deps.requireSession();
        const seed = findRun(id);
        const metas = RUN_STEP_META[id];
        return deps.delay(
          Array.from({ length: seed.steps.total }, (_, index) => {
            const done = index < seed.steps.completed;
            const failed = !done && seed.steps.failed > 0 && index === seed.steps.completed;
            const stepMeta = metas?.[index];
            const model = stepMeta?.model ?? "lclgemma";
            const totalTokens = stepMeta?.totalTokens ?? 4_210;
            const prompt = stepMeta?.prompt ?? `Node ${index + 1}: do the work.`;
            return {
              index,
              nodeId: seed.graph?.nodes[index]?.id ?? `node-${index + 1}`,
              name: seed.graph?.nodes[index]?.name ?? `Step ${index + 1}`,
              status: done ? "completed" : failed ? "failed" : "running",
              startedAt: hoursAgo(seed.createdHoursAgo),
              endedAt: done || failed ? hoursAgo(seed.createdHoursAgo - 0.05) : null,
              durationMs: done || failed ? 21_000 : null,
              output: done ? "The step produced a table of eleven rows." : "",
              outputTruncated: false,
              error: failed ? (seed.error ?? "The step failed.") : null,
              model,
              tokens: done ? totalTokens : null,
              meta:
                done || !failed ? { model, totalTokens, prompt, promptChars: prompt.length } : null,
              log: [{ at: hoursAgo(seed.createdHoursAgo), message: `▶ Step ${index + 1} started` }],
            };
          }),
        );
      },

      logs: async (id: string): Promise<RunLogLine[]> => {
        deps.requireSession();
        const seed = findRun(id);
        const lines: RunLogLine[] = [];
        for (let index = 0; index < seed.steps.total; index += 1) {
          lines.push({
            id: String(lines.length),
            at: hoursAgo(seed.createdHoursAgo),
            nodeId: `node-${index + 1}`,
            level: "info",
            message: `▶ Step ${index + 1}`,
          });
        }
        if (seed.error) {
          lines.push({
            id: String(lines.length),
            at: hoursAgo(seed.createdHoursAgo - 0.05),
            nodeId: null,
            level: "error",
            message: `Run failed: ${seed.error}`,
          });
        }
        return deps.delay(lines);
      },

      stats: async (query: { since?: string; kind?: string } = {}): Promise<RunStats> => {
        deps.requireSession();
        const scoped = query.kind ? runs.filter((run) => run.kind === query.kind) : runs;
        // Composed the same way toRun() reports it — completed-degraded gets its OWN bucket
        // (§B.3 precedence: failed > completed-degraded > completed), never folded into
        // "completed", which is exactly what would make it invisible.
        const reportedStatus = (seed: RunSeed): string =>
          seed.status === "completed" && seed.degradedSources ? "completed-degraded" : seed.status;

        const byStatus: Record<string, number> = {
          requested: 0,
          running: 0,
          observing: 0,
          paused: 0,
          completed: 0,
          "completed-degraded": 0,
          failed: 0,
          cancelled: 0,
        };
        for (const run of scoped) {
          const status = reportedStatus(run);
          byStatus[status] = (byStatus[status] ?? 0) + 1;
        }
        const byKind: Record<string, number> = {
          digest: 0,
          flow: 0,
          "pulse-advisor": 0,
          "pulse-backtest": 0,
          "pulse-strategy-backtest": 0,
          sweep: 0,
        };
        for (const run of scoped) {
          byKind[run.kind] = (byKind[run.kind] ?? 0) + 1;
        }
        const successCount = (byStatus.completed ?? 0) + (byStatus["completed-degraded"] ?? 0);
        const terminal = successCount + (byStatus.failed ?? 0) + (byStatus.cancelled ?? 0);
        const durations = scoped
          .map((run) => run.durationMs)
          .filter((ms): ms is number => ms !== null);

        return deps.delay({
          windowDays: 14,
          totalRuns: scoped.length,
          byStatus,
          byKind,
          byName: [
            {
              name: "Morning brief",
              count: 3,
              successRate: 0.67,
              avgDurationMs: 400_000,
              tokens: 96_700_000,
            },
            {
              name: "Release notes from the changelog",
              count: 2,
              successRate: 0.5,
              avgDurationMs: 450_000,
              tokens: 3_100,
            },
          ],
          successRate: terminal > 0 ? successCount / terminal : 0,
          terminalRuns: terminal,
          avgDurationMs: durations.length
            ? Math.round(durations.reduce((sum, ms) => sum + ms, 0) / durations.length)
            : null,
          maxDurationMs: durations.length ? Math.max(...durations) : null,
          sampledRuns: durations.length,
          tokens: { input: 2_100_000, output: 1_450_000, total: 98_500_000 },
          daily: Array.from({ length: 14 }, (_, index) => ({
            date: new Date(BASE_TIME - (13 - index) * 86_400_000).toISOString().slice(0, 10),
            total: index % 4,
            completed: index % 3,
            failed: index % 5 === 0 ? 1 : 0,
          })),
        });
      },

      control: async (id: string, action: RunControl): Promise<void> => {
        deps.requireSession();
        const seed = findRun(id);
        const ability = { pause: "pause", resume: "resume", cancel: "cancel", retry: "retry" }[
          action
        ];
        if (abilitiesFor(seed)[ability] !== true) {
          // The same refusal the platform makes: the verb has nowhere to land on this run.
          throw new ForbiddenError("That is not available on this run");
        }
        seed.status =
          action === "pause"
            ? "paused"
            : action === "resume"
              ? "running"
              : action === "cancel"
                ? "cancelled"
                : "running";
        if (action === "retry") {
          seed.steps = { total: seed.steps.total, completed: 0, failed: 0 };
          delete seed.error;
        }
        return deps.delay(undefined);
      },

      approve: async (id: string, decision: RunApproval): Promise<void> => {
        deps.requireSession();
        const seed = findRun(id);
        if (!seed.awaitingApprovalNodeId) {
          throw new ForbiddenError("This run is not waiting on an approval right now");
        }
        if (decision.nodeId && decision.nodeId !== seed.awaitingApprovalNodeId) {
          // A stale panel answering the previous node is real; refusing by id makes it harmless.
          throw new ForbiddenError(`This run is waiting on '${seed.awaitingApprovalNodeId}'`);
        }
        delete seed.awaitingApprovalNodeId;
        seed.status = decision.approved ? "running" : "cancelled";
        return deps.delay(undefined);
      },

      /**
       * Two verbs, never one ambiguous button. The fixture does not fully simulate cooperative
       * vs immediate honoring — it enforces the same "not on this run" gate every other verb
       * does, so a permission bug fails here too.
       */
      stop: async (
        id: string,
        mode: "graceful" | "immediate",
        _reason?: string,
      ): Promise<StopAck> => {
        deps.requireSession();
        const seed = findRun(id);
        if (abilitiesFor(seed)[ABILITY.runCancel] !== true) {
          throw new ForbiddenError("That is not available on this run");
        }
        seed.status = mode === "immediate" ? "cancelled" : "running";
        return deps.delay({
          ok: true,
          mode,
          honoredAt: mode === "immediate" ? "immediately" : "next-step-boundary",
          executor: seed.ideSessionKey ? { ideSessionKey: seed.ideSessionKey, pid: 4242 } : null,
          executorNote:
            mode === "immediate" ? "Cancelled immediately." : "Will stop at the next safe point.",
        });
      },

      step: async (id: string, options: { stop?: boolean } = {}): Promise<StepAck> => {
        deps.requireSession();
        const seed = findRun(id);
        if (abilitiesFor(seed)[ABILITY.runStep] !== true) {
          throw new ForbiddenError(
            "This run has no registered IDE executor polling a control gate, so stepping it would do nothing.",
          );
        }
        return deps.delay({
          ok: true,
          stepMode: !options.stop,
          released: options.stop ? true : null,
          honoredAt: "at the executor's next poll (about 2s)",
          executor: { ideSessionKey: seed.ideSessionKey, pid: 4242 },
          host: "ide",
        });
      },

      jump: async (id: string, target: JumpRequest): Promise<JumpAck> => {
        deps.requireSession();
        const seed = findRun(id);
        if (abilitiesFor(seed)[ABILITY.runJump] !== true) {
          if (seed.status === "running") {
            throw new AutomationError("run_is_live", "Pause the run before moving its cursor.");
          }
          throw new ForbiddenError("That is not available on this run");
        }
        // "Only nodes the run recorded a step for" (§A.4) — the same ids `runs.steps()` returns.
        const known =
          seed.graph?.nodes.map((node) => node.id) ??
          Array.from({ length: seed.steps.total }, (_, index) => `node-${index + 1}`);
        if (!known.includes(target.toNodeId)) {
          throw new AutomationError(
            "unknown_node",
            `'${target.toNodeId}' has no recorded step on this run.`,
            400,
            known,
          );
        }
        seed.currentNodeId = target.toNodeId;
        return deps.delay({
          ok: true,
          mode: target.mode ?? "rewind",
          toNodeId: target.toNodeId,
          invalidated: [],
          warning: null,
          executor: seed.ideSessionKey ? { ideSessionKey: seed.ideSessionKey, pid: 4242 } : null,
          executorNote: "",
        });
      },

      /** Always the FULL list; `[]` clears every breakpoint. */
      breakpoints: async (id: string, nodeIds: string[]): Promise<string[]> => {
        deps.requireSession();
        const seed = findRun(id);
        seed.breakpoints = [...nodeIds];
        return deps.delay([...nodeIds]);
      },

      /** Only `pulse-*` kinds may retry (F-5) — `can.retry` already says so before this is called. */
      retry: async (id: string): Promise<{ runId: string }> => {
        deps.requireSession();
        const seed = findRun(id);
        if (abilitiesFor(seed)[ABILITY.runRetry] !== true) {
          throw new ForbiddenError("This run kind cannot be retried");
        }
        return deps.delay({ runId: seed.id });
      },

      publish: async (id: string, options: PublishOptions = {}): Promise<PublishResult> => {
        deps.requireSession();
        findRun(id);
        if (!options.destination) {
          throw new AutomationError(
            "no_destination",
            "This flow version declares no output destination — pass one in `destination`.",
          );
        }
        if (options.destination.mode !== "create" && !options.destination.parentDocumentId) {
          throw new AutomationError(
            "bad_destination",
            "Append/replace need a target document id — that IS the document being changed.",
          );
        }
        return deps.delay({
          ok: true,
          documentId: options.destination.parentDocumentId ?? "doc-published-1",
          title: options.destination.titleTemplate ?? "Published run",
          url: `/doc/${options.destination.parentDocumentId ?? "doc-published-1"}`,
          mode: options.destination.mode ?? "create",
        });
      },

      artifacts: async (id: string): Promise<RunArtifact[]> => {
        deps.requireSession();
        findRun(id);
        const seeds = RUN_ARTIFACTS[id] ?? [];
        return deps.delay(
          seeds.map((entry) => ({
            path: entry.path,
            version: entry.version,
            nodeId: entry.nodeId,
            mediaType: entry.mediaType,
            bytes: entry.bytes,
            sha256: null,
            producedBy: entry.nodeId,
            createdAt: hoursAgo(0.2),
            hasContent: entry.hasContent,
            sensitive: entry.sensitive,
          })),
        );
      },

      artifactContent: async (id: string, path: string, version = 1): Promise<ArtifactContent> => {
        deps.requireSession();
        findRun(id);
        const entry = (RUN_ARTIFACTS[id] ?? []).find(
          (candidate) => candidate.path === path && candidate.version === version,
        );
        if (!entry) {
          throw new NotFoundError("not_found");
        }
        // Raised to `run` for a sensitive artifact — never just `read` (§B.3).
        if (entry.sensitive && manage() !== true) {
          throw new ForbiddenError("This artifact needs elevated access.");
        }
        return deps.delay({
          path: entry.path,
          version: entry.version,
          nodeId: entry.nodeId,
          mediaType: entry.mediaType,
          bytes: entry.bytes,
          sha256: null,
          producedBy: entry.nodeId,
          createdAt: hoursAgo(0.2),
          content: entry.contentWithheld ? null : entry.content,
          contentWithheld: entry.contentWithheld,
        });
      },
    },

    /** Directives: forward-only steering messages a live flow's nodes can read. */
    directives: {
      list: async (flowId: string, activeOnly = true): Promise<Directive[]> => {
        deps.requireSession();
        return deps.delay(
          directives
            .filter((entry) => entry.flowId === flowId)
            .filter((entry) => (activeOnly ? entry.active : true))
            .map(toDirective),
        );
      },

      create: async (flowId: string, text: string): Promise<Directive> => {
        deps.requireSession();
        const entry: DirectiveSeed = {
          id: `dir-${directives.length + 1}`,
          flowId,
          text,
          active: true,
          createdBy: "You",
          createdHoursAgo: 0,
        };
        directives = [...directives, entry];
        return deps.delay(toDirective(entry));
      },

      revoke: async (id: string): Promise<void> => {
        deps.requireSession();
        directives = directives.map((entry) =>
          entry.id === id ? { ...entry, active: false, revokedHoursAgo: 0 } : entry,
        );
        return deps.delay(undefined);
      },
    },

    tasks: {
      list: async (): Promise<ScheduledTask[]> => {
        deps.requireSession();
        const abilities: Abilities = manage() ? { [ABILITY.taskManage]: true } : {};
        return deps.delay(tasks.map((task) => ({ ...task, abilities })));
      },

      create: async (draft: ScheduledTaskDraft): Promise<void> => {
        deps.requireSession();
        requireManage("You cannot schedule work in this workspace");
        findFlow(draft.flowSlug);
        tasks = [
          ...tasks,
          {
            id: `task-${tasks.length + 1}`,
            title: draft.title,
            taskType: "ide-flow",
            flowId: draft.flowSlug,
            triggerKind: draft.trigger.kind,
            recurrence: draft.trigger.kind === "every" ? `${draft.trigger.everyMinutes}m` : null,
            onEvent: draft.trigger.kind === "on" ? draft.trigger.onEvent : null,
            paused: false,
            nextFireAt: draft.trigger.kind === "at" ? draft.trigger.atUtc : hoursAhead(1),
            lastFiredAt: null,
            abilities: {},
          },
        ];
        return deps.delay(undefined);
      },

      /** Task Center's digest-authoring mode: a topic/source set that writes into a document. */
      createDigest: async (draft: DigestTaskDraft): Promise<void> => {
        deps.requireSession();
        requireManage("You cannot schedule work in this workspace");
        if (!draft.topic?.trim() && (draft.sources ?? []).length === 0) {
          throw new AutomationError(
            "digest_topic_required",
            "Provide a digest.topic (for search) or at least one source.",
          );
        }
        tasks = [
          ...tasks,
          {
            id: `task-${tasks.length + 1}`,
            title: draft.title ?? draft.topic ?? "Digest",
            taskType: "digest",
            flowId: null,
            triggerKind: draft.trigger.kind,
            recurrence: draft.trigger.kind === "every" ? `${draft.trigger.everyMinutes}m` : null,
            onEvent: draft.trigger.kind === "on" ? draft.trigger.onEvent : null,
            paused: false,
            nextFireAt: draft.trigger.kind === "at" ? draft.trigger.atUtc : hoursAhead(1),
            lastFiredAt: null,
            abilities: {},
          },
        ];
        return deps.delay(undefined);
      },

      /** Task Center's plain-flow mode — no IDE window needed for the flow to fire. */
      createFlow: async (draft: FlowTaskDraft): Promise<void> => {
        deps.requireSession();
        requireManage("You cannot schedule work in this workspace");
        findFlow(draft.flowId);
        tasks = [
          ...tasks,
          {
            id: `task-${tasks.length + 1}`,
            title: draft.title,
            taskType: "flow",
            flowId: draft.flowId,
            triggerKind: draft.trigger.kind,
            recurrence: draft.trigger.kind === "every" ? `${draft.trigger.everyMinutes}m` : null,
            onEvent: draft.trigger.kind === "on" ? draft.trigger.onEvent : null,
            paused: false,
            nextFireAt: draft.trigger.kind === "at" ? draft.trigger.atUtc : hoursAhead(1),
            lastFiredAt: null,
            abilities: {},
          },
        ];
        return deps.delay(undefined);
      },

      setPaused: async (id: string, paused: boolean): Promise<void> => {
        deps.requireSession();
        requireManage("You cannot change scheduled work in this workspace");
        const task = findTask(id);
        tasks = tasks.map((entry) =>
          entry.id === task.id
            ? { ...entry, paused, nextFireAt: paused ? null : hoursAhead(1) }
            : entry,
        );
        return deps.delay(undefined);
      },

      runNow: async (id: string): Promise<{ note: string | null }> => {
        deps.requireSession();
        requireManage("You cannot run scheduled work in this workspace");
        const task = findTask(id);
        const note =
          task.taskType === "ide-flow"
            ? "queued; a connected IDE window claims it within ~5 s"
            : task.taskType === "digest"
              ? "digest queued"
              : "run requested";
        return deps.delay({ note });
      },

      delete: async (id: string): Promise<void> => {
        deps.requireSession();
        requireManage("You cannot delete scheduled work in this workspace");
        findTask(id);
        tasks = tasks.filter((task) => task.id !== id);
        return deps.delay(undefined);
      },

      /**
       * The prompt composer: propose → confirm → create, never propose → create directly.
       *
       * ONE endpoint, SEVEN shapes. The fixture answers a DIFFERENT intent per keyword so
       * every confirm card, every `missing[]` warning and both ends of the navigate confidence
       * ladder are reachable with no backend — the routing tree is this wave's logic, and logic
       * that can only be exercised against a live model is logic nobody tests.
       *
       * All five surfaces are recognised. The previous fixture refused everything but
       * `tasks`/`document` (§E6.8), which would have made a feature gated on `flows` or
       * `collection` look broken in tests and work in production.
       */
      route: async (
        prompt: string,
        surface: RouteSurface = "tasks",
        routeId?: string,
      ): Promise<RouteProposal> => {
        deps.requireSession();
        const text = prompt.trim();
        if (!text) {
          throw new AutomationError("prompt_required", "A prompt is required.");
        }
        if (!ROUTE_SURFACES.includes(surface)) {
          return deps.delay({
            ...routeCommon(text, surface, 0.7),
            actionId: null,
            // The server sends the surface NAME here, never a flag (§E6.7).
            unsupportedSurface: surface,
            message: "The prompt bar can't act on this screen yet.",
          });
        }
        const q = text.toLowerCase();
        const canUpdate = deps.abilities()[ABILITY.workspaceUpdate] === true;

        if (q.includes("remind") || q.includes("nudge")) {
          const past = q.includes("yesterday");
          const when = past ? null : new Date(BASE_TIME + 86_400_000).toISOString();
          return deps.delay({
            ...routeCommon(text, surface, 0.86),
            actionId: "create-nudge",
            nudge: {
              targetType: "document",
              documentId: surface === "document" ? (routeId ?? null) : null,
              whenUtc: when,
              timezone: "UTC",
              recurrenceRule: q.includes("every day") ? "FREQ=DAILY" : null,
              note: text,
            },
            missing: [
              ...(surface === "document" && routeId ? [] : ["documentId"]),
              ...(when ? [] : ["whenUtc"]),
            ],
          });
        }

        if (
          q.includes("go to") ||
          q.includes("open ") ||
          q.includes("switch to") ||
          q.includes("take me to") ||
          q.includes("where is") ||
          /^(show|find)\b/.test(q)
        ) {
          const { kind, needle } = navigateNeedle(q);
          const matched = needle
            ? NAVIGATE_FIXTURES.filter((entry) => entry.title.toLowerCase().includes(needle))
            : [];
          // Hinted kind first, then the fixture order — the server's `NavigateTargetRanking.Rank`.
          const hits = [...matched].sort(
            (a, b) =>
              Number(kind !== null && b.kind === kind) - Number(kind !== null && a.kind === kind),
          );
          // The ladder counts the hinted kind's hits when the hint found any — the rest are alternates.
          const counted =
            kind !== null && hits.some((entry) => entry.kind === kind)
              ? hits.filter((entry) => entry.kind === kind).length
              : hits.length;
          if (hits.length > 0) {
            const top = hits[0] as NavigateTarget;
            const exact = top.title.toLowerCase() === needle;
            const confidence = counted === 1 ? (exact ? 0.95 : 0.85) : exact ? 0.9 : 0.55;
            return deps.delay({
              ...routeCommon(text, surface, confidence),
              actionId: "navigate",
              document: { ...top },
              alternates: hits.slice(1).map((entry) => ({ ...entry })),
            });
          }
          return deps.delay({
            ...routeCommon(text, surface, 0),
            actionId: "navigate",
            document: null,
            missing: ["targetDocumentId"],
          });
        }

        if (q.includes("section")) {
          if (surface !== "document") {
            return deps.delay({
              ...routeCommon(text, surface, 0.7),
              actionId: null,
              unsupportedSurface: null,
              message: "Open a document first — sections are drafted into the page you're on.",
            });
          }
          if (!canUpdate) {
            // `BuildEditSectionProposalAsync` 403s `edit_refused` for a viewer. A slate
            // regression that renders the card for a reader must fail a test, not a user.
            throw new ForbiddenError(
              "You can view this document but not edit it — drafting a section needs edit access.",
            );
          }
          return deps.delay({
            ...routeCommon(text, surface, 0.78),
            actionId: "edit-section",
            section: {
              documentId: routeId ?? null,
              heading: "Risks",
              text: "The three risks worth naming are cost, latency and the migration window.",
              requestedLane: "propose",
            },
            missing: routeId ? [] : ["documentId"],
          });
        }

        if (q.includes("tell ") || q.includes("announce")) {
          return deps.delay({
            ...routeCommon(text, surface, 0.72),
            actionId: "notify",
            notify: {
              kind: "documents.nudge",
              message: text,
              audienceRole: "member",
              documentId: surface === "document" ? (routeId ?? null) : null,
              collectionId: null,
              audienceSummary: "members with access to this document",
            },
          });
        }

        if (q.includes("new doc") || q.includes("create a document")) {
          const named = /(?:called|named|about)\s+(.+)$/.exec(text);
          const title = named?.[1]?.trim() ?? null;
          return deps.delay({
            ...routeCommon(text, surface, 0.8),
            actionId: "create-document",
            document: {
              title,
              text: title ? `# ${title}\n\n` : null,
              collectionId: surface === "collection" ? (routeId ?? null) : "col-1",
              collectionTitle: surface === "collection" ? null : "Engineering",
            },
            missing: title ? [] : ["title"],
          });
        }

        if (q.includes("taskize") || q.includes("turn this into tasks")) {
          if (surface !== "document") {
            return deps.delay({
              ...routeCommon(text, surface, 0.7),
              actionId: null,
              unsupportedSurface: null,
              message: "Open a document first.",
            });
          }
          return deps.delay({ ...routeCommon(text, surface, 0.9), actionId: "taskize" });
        }

        return deps.delay({
          ...routeCommon(text, surface, 0.82),
          actionId: "create-digest",
          digest: {
            topic: text,
            sourceMode: "search",
            sources: [],
            thinkerId: "lclgemma",
            targetDocumentId: null,
            targetDocumentTitle: null,
            outputMode: "prepend",
            notifyInApp: true,
            maxSources: 4,
          },
          trigger: { kind: "every", everyMinutes: 1440 },
          missing: ["targetDocumentId"],
        });
      },
    },

    dispatches: {
      list: async (options: { activeOnly?: boolean; limit?: number } = {}): Promise<Dispatch[]> => {
        deps.requireSession();
        const active = new Set(["queued", "claimed", "running"]);
        return deps.delay(
          dispatchSeeds
            .filter((entry) => (options.activeOnly ? active.has(entry.status) : true))
            .slice(0, options.limit ?? 25)
            .map((entry) => ({ ...entry, run: entry.run ? { ...entry.run } : null })),
        );
      },

      cancel: async (id: string, _reason?: string): Promise<void> => {
        deps.requireSession();
        requireManage("You cannot cancel dispatched work in this workspace");
        const found = dispatchSeeds.find((entry) => entry.id === id);
        if (!found) {
          throw new NotFoundError("not_found");
        }
        found.status = "cancelled";
        found.statusDetail = "cancelled";
        return deps.delay(undefined);
      },

      /**
       * Fixture escape hatch, not part of `ApiClient`: the LIVE seed array, so W4's
       * `ai-dispatch.get` reads the same rows `.list` serves. Two arrays would let one surface
       * show a dispatch as running while the other shows it cancelled — a fixture disagreeing
       * with itself is worse than no fixture.
       */
      rows: (): DispatchSeed[] => dispatchSeeds,
    },

    /** Connected editor windows — Task Center's target-window filter. */
    ideSessions: {
      list: async (): Promise<IdeSession[]> => {
        deps.requireSession();
        // `capabilities` is a nested array: cloned, or a test that reads a policy badge and a
        // test that seeds one bleed into each other.
        return deps.delay(
          IDE_SESSION_SEEDS.map((entry) => ({ ...entry, capabilities: [...entry.capabilities] })),
        );
      },
    },

    activity: {
      list: async (query: ActivityQuery = {}): Promise<ActivityPage> => {
        deps.requireSession();
        if (query.auditLog && deps.abilities()[ABILITY.workspaceViewAudit] !== true) {
          throw new ForbiddenError("You cannot read the audit log in this workspace");
        }
        const offset = query.offset ?? 0;
        const limit = query.limit ?? 25;
        return deps.delay({
          events: events.slice(offset, offset + limit).map((event) => ({ ...event })),
          offset,
          limit,
          total: events.length,
        });
      },
    },
  };
}
